import React, { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../primitives/Popover";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { ImageOption, ImageOptionPickerProps } from "./ImageOptionPicker.types";
import { ImageOptionPickerTrigger } from "./ImageOptionPickerTrigger";
import { ImageOptionPreviewDialog } from "./ImageOptionPreviewDialog";
import { ImageOptionRow } from "./ImageOptionRow";
import { toggleOptionSelection } from "./toggleOptionSelection";

/**
 * Select control for options that are primarily visual: the trigger shows the
 * picked option with its thumbnail, and the dropdown lists every option with a
 * large image plus a button to preview it full size.
 *
 * Use it when *every* option carries an image — with only some images present, a
 * plain select with inline thumbnails reads better. It is a dedicated control
 * rather than a `UnifiedSelect` with a custom `renderItem` because that renders
 * items inside the row's own button, which cannot hold the preview button.
 */
export const ImageOptionPicker: React.FC<ImageOptionPickerProps> = ({
  options,
  value,
  onChangeValue,
  multiselect = false,
  groupLabel,
  ...wrapperProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [previewOption, setPreviewOption] = useState<ImageOption | null>(null);

  const selectedOptions = options.filter((option) => value.includes(option.id));
  const listLabel =
    groupLabel ??
    (typeof wrapperProps.label === "string" ? wrapperProps.label : undefined);

  const toggle = (id: string) => {
    onChangeValue(toggleOptionSelection(value, id, multiselect));
    // Single select closes on pick, the way a plain select does; multiselect
    // stays open so several options can be picked in one go.
    if (!multiselect) {
      setIsOpen(false);
    }
  };

  /**
   * Popovers sit above dialogs in the z-index scale, so the list has to close
   * for the preview to be visible. It reopens afterwards to put the user back
   * where they were. Closing beats raising the dialog's z-index, which would
   * only hold for as long as nothing else on screen stacks higher — this picker
   * is used inside modals and drawers that bring their own stacking.
   */
  const openPreview = (option: ImageOption) => {
    setIsOpen(false);
    setPreviewOption(option);
  };

  const closePreview = () => {
    setPreviewOption(null);
    setIsOpen(true);
  };

  return (
    <FormControlWrapper
      variant="unstyled"
      wrapperClassName="py-0"
      {...wrapperProps}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <ImageOptionPickerTrigger
            selectedOptions={selectedOptions}
            isOpen={isOpen}
            isDisabled={wrapperProps.isDisabled}
            label={
              typeof wrapperProps.label === "string"
                ? wrapperProps.label
                : undefined
            }
            placeholder={
              wrapperProps.placeholder ??
              (multiselect ? "Select options..." : "Select an option...")
            }
          />
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={6}
          showArrow={false}
          className="max-h-80 w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1.5"
          aria-label={listLabel ?? "Options"}
        >
          <ul aria-label={listLabel} className="flex flex-col gap-0.5">
            {options.map((option) => (
              <ImageOptionRow
                key={option.id}
                option={option}
                isSelected={value.includes(option.id)}
                onToggle={() => toggle(option.id)}
                onPreview={
                  option.imageUrl ? () => openPreview(option) : undefined
                }
              />
            ))}
          </ul>
        </PopoverContent>
      </Popover>

      <FormControlOutline />

      <ImageOptionPreviewDialog option={previewOption} onClose={closePreview} />
    </FormControlWrapper>
  );
};
